import { useState, useCallback } from 'react';
import { Block } from '../lib/types';

/** Remonte le DOM depuis .block-selected pour trouver un élément avec id (ex. note-block). */
function collectSelectedBlockIdsFromDom(): string[] {
  const ids = new Set<string>();
  document.querySelectorAll('.block-selected').forEach((el) => {
    let node: Element | null = el;
    for (let depth = 0; depth < 8 && node; depth++) {
      const id = (node as HTMLElement).id?.trim();
      if (id) {
        ids.add(id);
        break;
      }
      node = node.parentElement;
    }
  });
  return Array.from(ids);
}

export const useClipboardActions = (
  blocks: Block[],
  setBlocks: React.Dispatch<React.SetStateAction<Block[]>>,
  swimlaneRef: React.RefObject<any>,
  /** Sélection courante depuis App (onBlockSelect) — source fiable car SwimlaneHeaders n’expose pas getSelectedBlock */
  selectedBlock: Block | null,
  setSelectedBlock?: React.Dispatch<React.SetStateAction<Block | null>>
) => {
  const [clipboard, setClipboard] = useState<Block[]>([]);

  // Helper function to generate a unique ID
  const generateUniqueId = (prefix: string) => `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

  const resolveSelectedBlocks = useCallback((): Block[] => {
    let selectedBlocks = blocks.filter((block) => block.selected);

    // Bloc actif dans la barre de propriétés (toujours à jour au clic)
    if (selectedBlocks.length === 0 && selectedBlock) {
      const fresh = blocks.find((b) => b.id === selectedBlock.id) || selectedBlock;
      selectedBlocks = [fresh];
    }

    if (selectedBlocks.length === 0 && swimlaneRef.current?.getSelectedBlock) {
      const currentSelectedBlock = swimlaneRef.current.getSelectedBlock();
      if (currentSelectedBlock) {
        selectedBlocks = [currentSelectedBlock];
      }
    }

    if (selectedBlocks.length === 0) {
      const domIds = collectSelectedBlockIdsFromDom();
      if (domIds.length > 0) {
        selectedBlocks = blocks.filter((block) => domIds.includes(block.id));
      }
    }

    if (selectedBlocks.length === 0 && swimlaneRef.current?.selectedBlock) {
      const currentBlock = swimlaneRef.current.selectedBlock;
      if (currentBlock) {
        selectedBlocks = [currentBlock];
      }
    }

    return selectedBlocks;
  }, [blocks, selectedBlock, swimlaneRef]);

  // Copy selected blocks
  const handleCopy = useCallback(() => {
    const selectedBlocks = resolveSelectedBlocks();

    if (selectedBlocks.length === 0) {
      return;
    }

    setClipboard(selectedBlocks);
    return selectedBlocks;
  }, [resolveSelectedBlocks]);

  // Cut selected blocks
  const handleCut = useCallback(() => {
    const selectedBlocks = resolveSelectedBlocks();

    if (selectedBlocks.length === 0) {
      return;
    }

    setClipboard(selectedBlocks);

    const selectedIds = selectedBlocks.map((block) => block.id);
    const deleted = swimlaneRef.current?.deleteBlocks?.(selectedIds) === true;
    if (!deleted) {
      const updatedBlocks = blocks.filter((block) => !selectedIds.includes(block.id));
      setBlocks(updatedBlocks);
      swimlaneRef.current?.updateData(undefined, updatedBlocks);
    }
    setSelectedBlock?.(null);

    return selectedBlocks;
  }, [blocks, resolveSelectedBlocks, setBlocks, setSelectedBlock, swimlaneRef]);

  // Paste blocks from clipboard
  const handlePaste = useCallback(() => {
    if (clipboard.length === 0) {
      // console.log('Clipboard is empty');
      return;
    }
    
    // Calculate offset to avoid direct overlap
    const OFFSET_X = 20;
    const OFFSET_Y = 20;
    
    // Create new blocks with new IDs and slightly offset positions
    const newBlocks = clipboard.map(block => {
      const newBlock: Block = {
        ...JSON.parse(JSON.stringify(block)), // Deep clone to avoid reference issues
        id: generateUniqueId(block.type),
        x: (block.x || 0) + OFFSET_X,
        y: (block.y || 0) + OFFSET_Y,
        selected: true // Select the newly pasted blocks
      };
      
      return newBlock;
    });
    
    // Deselect all existing blocks
    const updatedBlocks = blocks.map(block => ({
      ...block,
      selected: false
    }));
    
    // Add the new blocks
    const finalBlocks = [...updatedBlocks, ...newBlocks];
    setBlocks(finalBlocks);
    if (swimlaneRef.current?.addBlocks) {
      swimlaneRef.current.addBlocks(newBlocks);
    } else {
      swimlaneRef.current?.updateData(undefined, finalBlocks);
    }

    if (setSelectedBlock && newBlocks[0]) {
      setSelectedBlock(newBlocks[0]);
    }
    return newBlocks;
  }, [clipboard, blocks, setBlocks, swimlaneRef, setSelectedBlock]);

  return {
    clipboard,
    handleCopy,
    handleCut,
    handlePaste
  };
};